import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dropover,
  Flex,
  FormMessage,
  Label,
  Modal,
  Radio,
  Text,
  theme,
} from '@xometry/xometry_loft';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import * as SmartySDK from 'smartystreets-javascript-sdk';
import { MappedShippingAddress } from '../types';
import { mapSmartyAddress } from '../utilities';
import { shippingAddressSchema } from '../EditShippingAddressForm';

export type AddressCorrectionModalProps = {
  modalOpen: boolean;
  enteredAddress: MappedShippingAddress;
  smartySuggestedAddress: SmartySDK.usStreet.Candidate | null;
  noSmartyAddressFound: boolean;
  handleClose: (closeType: AddressCorrectionModalCloseType) => void;
  saveAddress: (address: MappedShippingAddress) => Promise<void>;
};

type AddressCorrectionFormFields = {
  addressSelection: 'enteredAddress' | 'suggestedAddress' | string;
  validationExempt: boolean;
};

export enum AddressCorrectionModalCloseType {
  cancel = 'cancel',
  continue = 'continue',
}

const AddressCorrectionModal = ({
  modalOpen,
  enteredAddress,
  smartySuggestedAddress,
  noSmartyAddressFound,
  handleClose,
  saveAddress,
}: AddressCorrectionModalProps) => {
  let suggestedAddress: MappedShippingAddress | null = null;

  if (smartySuggestedAddress?.components) {
    suggestedAddress = mapSmartyAddress(
      smartySuggestedAddress.components,
      enteredAddress
    );
  }

  const handleSelectAddress = async (values: AddressCorrectionFormFields) => {
    let addressToSet = {
      ...enteredAddress,
      validated: false,
      validationExempt: false,
    };
    if (suggestedAddress) {
      if (values.addressSelection === 'enteredAddress') {
        addressToSet = {
          ...enteredAddress,
          validated: false,
          validationExempt: true,
        };
      }
      if (values.addressSelection === 'suggestedAddress') {
        addressToSet = {
          ...suggestedAddress,
          validated: true,
          validationExempt: false,
        };
      }
    }
    if (noSmartyAddressFound) {
      addressToSet = {
        ...enteredAddress,
        validated: false,
        validationExempt: true,
      };
    }

    try {
      await shippingAddressSchema.validate(addressToSet);
      await saveAddress(addressToSet);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };
  return (
    <Modal
      isOpen={modalOpen}
      onClose={() => handleClose(AddressCorrectionModalCloseType.cancel)}
      maxWidth="500px"
    >
      <Modal.CloseButton
        position="absolute"
        top={theme.space[3]}
        right={theme.space[3]}
      />
      {!noSmartyAddressFound && (
        <Formik
          initialValues={{
            addressSelection: '',
            validationExempt: false,
          }}
          validationSchema={yup.object().shape({
            addressSelection: yup
              .string()
              .required('Please select an Address.')
              .oneOf(['enteredAddress', 'suggestedAddress']),
            validationExempt: yup
              .bool()
              .when('addressSelection', ([addressSelection], schema) => {
                if (addressSelection === 'enteredAddress') {
                  return schema
                    .required()
                    .oneOf([true], 'This Field is Required');
                }
                return schema;
              }),
          })}
          onSubmit={(values) => handleSelectAddress(values)}
        >
          {({ values, handleChange, errors, touched }) => (
            <Form>
              <Flex
                flexDirection="column"
                gap={4}
                width={['200px', '300px', '400px', '500px']}
              >
                <Flex flexDirection="column" gap={4}>
                  {enteredAddress && (
                    <>
                      <Text
                        kind="body-title"
                        color={theme.colors.darkBlues[75]}
                      >
                        You entered:
                      </Text>
                      <Flex flexDirection="row" alignItems="baseline">
                        {!noSmartyAddressFound && (
                          <Radio
                            id="enteredAddress"
                            name="addressSelection"
                            value="enteredAddress"
                            onChange={handleChange}
                          />
                        )}
                        <Label
                          htmlFor="enteredAddress"
                          marginLeft={theme.space[2]}
                          scale={theme.scale.large}
                          fontWeight={theme.fontWeights.regular}
                        >
                          <Flex flexDirection="column">
                            <span>{enteredAddress.street1}</span>
                            {enteredAddress.street2 ? (
                              <span>{enteredAddress.street2}</span>
                            ) : (
                              ''
                            )}
                            <span>
                              {enteredAddress.city},&nbsp;
                              {enteredAddress.state}&nbsp;
                              {enteredAddress.zipcode}
                            </span>
                          </Flex>
                        </Label>
                      </Flex>
                    </>
                  )}
                  {suggestedAddress && (
                    <>
                      <Flex alignItems="center" flex="1">
                        <Text
                          kind="body-title"
                          color={theme.colors.darkBlues[75]}
                          marginRight="8px"
                        >
                          We recommend:
                        </Text>
                        <Dropover alignment="center">
                          <Dropover.Trigger>
                            <Flex flexDirection="row">
                              <Box
                                color={theme.colors.greens[100]}
                                fontSize="20px"
                              >
                                <i className="fas fa-check-circle" />
                                &nbsp;
                              </Box>
                            </Flex>
                          </Dropover.Trigger>
                          <Dropover.Content
                            width="max-content"
                            fontWeight={theme.fontWeights.semibold}
                          >
                            Verified Address
                          </Dropover.Content>
                        </Dropover>
                      </Flex>
                      <Flex flexDirection="row" alignItems="baseline">
                        <Radio
                          id="suggestedAddress"
                          name="addressSelection"
                          value="suggestedAddress"
                          onChange={handleChange}
                        />
                        <Label
                          htmlFor="suggestedAddress"
                          marginLeft={theme.space[2]}
                          scale={theme.scale.large}
                          fontWeight={theme.fontWeights.regular}
                        >
                          <Flex flexDirection="column">
                            <span>{suggestedAddress.street1}</span>
                            {suggestedAddress.street2 ? (
                              <span>{suggestedAddress.street2}</span>
                            ) : (
                              ''
                            )}
                            <span>
                              {suggestedAddress.city},&nbsp;
                              {suggestedAddress.state}&nbsp;
                              {suggestedAddress.zipcode}
                            </span>
                          </Flex>
                        </Label>
                      </Flex>
                    </>
                  )}
                </Flex>
                {touched.validationExempt && errors.addressSelection && (
                  <FormMessage invalid>{errors.addressSelection}</FormMessage>
                )}
                {(values.addressSelection === 'enteredAddress' ||
                  noSmartyAddressFound) && (
                  <Flex
                    flexDirection="row"
                    gap={theme.space[2]}
                    alignItems="center"
                  >
                    <Checkbox
                      id="validationExempt"
                      onChange={handleChange}
                      invalid={Boolean(
                        touched.validationExempt && errors.validationExempt
                      )}
                    />
                    <Label
                      marginBottom="0"
                      htmlFor="validationExempt"
                      fontWeight={theme.fontWeights.regular}
                    >
                      I understand submitting an unverified address may require
                      further validation to fulfill this order.
                    </Label>
                  </Flex>
                )}
                {touched.validationExempt && errors.validationExempt && (
                  <FormMessage invalid>{errors.validationExempt}</FormMessage>
                )}
                <Flex flexDirection="row" justifyContent="end" gap={2}>
                  <Button
                    type="button"
                    onClick={() =>
                      handleClose(AddressCorrectionModalCloseType.cancel)
                    }
                  >
                    Cancel
                  </Button>
                  <Button type="submit" kind={theme.kind.primary}>
                    {values.addressSelection === 'suggestedAddress' &&
                      'Save and Continue'}
                    {values.addressSelection === 'enteredAddress' && 'Continue'}
                    {values.addressSelection === '' && 'Continue'}
                  </Button>
                </Flex>
              </Flex>
            </Form>
          )}
        </Formik>
      )}
      {noSmartyAddressFound && (
        <Formik
          initialValues={{
            validationExempt: false,
          }}
          validationSchema={yup.object().shape({
            validationExempt: yup
              .bool()
              .required()
              .oneOf([true], 'This Field is Required'),
          })}
          onSubmit={(values) =>
            handleSelectAddress({
              ...values,
              addressSelection: 'enteredAddress',
            })
          }
        >
          {({ handleChange, errors, touched }) => (
            <Form>
              <Flex
                flexDirection="column"
                gap={4}
                width={['200px', '300px', '400px', '500px']}
              >
                <Flex flexDirection="column" gap={4}>
                  {enteredAddress && (
                    <>
                      <Text
                        kind="body-title"
                        color={theme.colors.darkBlues[75]}
                      >
                        You entered:
                      </Text>
                      <Flex flexDirection="row" alignItems="baseline">
                        <Label
                          htmlFor="enteredAddress"
                          scale={theme.scale.large}
                          fontWeight={theme.fontWeights.regular}
                        >
                          <Flex flexDirection="column">
                            <span>{enteredAddress.street1}</span>
                            {enteredAddress.street2 ? (
                              <span>{enteredAddress.street2}</span>
                            ) : (
                              ''
                            )}
                            <span>
                              {enteredAddress.city},&nbsp;
                              {enteredAddress.state}&nbsp;
                              {enteredAddress.zipcode}
                            </span>
                          </Flex>
                        </Label>
                      </Flex>
                    </>
                  )}
                  {!suggestedAddress && (
                    <Text kind="body-sm" color={theme.colors.reds[100]}>
                      The address you entered could not be verified.
                    </Text>
                  )}
                </Flex>
                <Flex
                  flexDirection="row"
                  gap={theme.space[2]}
                  alignItems="center"
                >
                  <Checkbox
                    id="validationExempt"
                    onChange={handleChange}
                    invalid={Boolean(
                      touched.validationExempt && errors.validationExempt
                    )}
                  />
                  <Label
                    marginBottom="0"
                    htmlFor="validationExempt"
                    fontWeight={theme.fontWeights.regular}
                  >
                    I understand submitting an unverified address may require
                    further validation to fulfill this order.
                  </Label>
                </Flex>
                {touched.validationExempt && errors.validationExempt && (
                  <FormMessage invalid>{errors.validationExempt}</FormMessage>
                )}
                <Flex flexDirection="row" justifyContent="end" gap={2}>
                  <Button
                    type="button"
                    onClick={() =>
                      handleClose(AddressCorrectionModalCloseType.cancel)
                    }
                  >
                    Cancel
                  </Button>
                  <Button type="submit" kind={theme.kind.primary}>
                    Continue
                  </Button>
                </Flex>
              </Flex>
            </Form>
          )}
        </Formik>
      )}
    </Modal>
  );
};

export default AddressCorrectionModal;
